import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Copy, Save, UserPlus, Coffee, CalendarOff, Clock, User, Upload } from "lucide-react";
import api, { fmtErr } from "../../lib/api";

const DAYS = [
  { key: "0", label: "Segunda" },
  { key: "1", label: "Terça" },
  { key: "2", label: "Quarta" },
  { key: "3", label: "Quinta" },
  { key: "4", label: "Sexta" },
  { key: "5", label: "Sábado" },
  { key: "6", label: "Domingo" },
];

const inputCls =
  "bg-white border border-black/15 px-2.5 py-1.5 text-sm font-light focus:outline-none focus:border-black";

const Toggle = ({ checked, onChange, label, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={() => onChange(!checked)}
    className="flex items-center gap-2 text-sm font-light"
  >
    <span
      className={`w-10 h-6 rounded-full transition-colors relative ${checked ? "bg-black" : "bg-black/20"}`}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${checked ? "translate-x-4" : ""}`}
      />
    </span>
    {label}
  </button>
);

const IntervalRow = ({ interval, onChange, onRemove, testid }) => (
  <div className="flex items-center gap-2" data-testid={testid}>
    <input
      type="time"
      value={interval.start}
      onChange={(e) => onChange({ ...interval, start: e.target.value })}
      className={`${inputCls} w-28`}
    />
    <span className="text-black/40">—</span>
    <input
      type="time"
      value={interval.end}
      onChange={(e) => onChange({ ...interval, end: e.target.value })}
      className={`${inputCls} w-28`}
    />
    <button type="button" onClick={onRemove} className="p-1.5 text-red-600 hover:bg-black/5" aria-label="Remover intervalo">
      <Trash2 size={14} />
    </button>
  </div>
);

const ProfessionalEditor = ({ prof, onSaved, onDeleted }) => {
  const [draft, setDraft] = useState(prof);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => setDraft(prof), [prof]);

  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));

  const handlePhotoUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("O ficheiro tem de ser uma imagem.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Imagem demasiado grande (máx. 10MB).");
      return;
    }
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set({ photo_url: res.data.url });
      toast.success("Foto carregada. Não se esqueça de guardar.");
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail) || "Falha no upload.");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const dayList = (bucket, day) => (draft[bucket]?.[day] || []);

  const updateDay = (bucket, day, list) =>
    set({ [bucket]: { ...(draft[bucket] || {}), [day]: list } });

  const addInterval = (bucket, day) =>
    updateDay(bucket, day, [...dayList(bucket, day), { start: "09:00", end: "13:00" }]);

  const changeInterval = (bucket, day, idx, val) => {
    const list = [...dayList(bucket, day)];
    list[idx] = val;
    updateDay(bucket, day, list);
  };

  const removeInterval = (bucket, day, idx) =>
    updateDay(bucket, day, dayList(bucket, day).filter((_, i) => i !== idx));

  const copyMondayToWeekdays = () => {
    const mon = draft.working_hours?.["0"] || [];
    const wh = { ...(draft.working_hours || {}) };
    ["1", "2", "3", "4", "5"].forEach((k) => (wh[k] = mon.map((iv) => ({ ...iv }))));
    set({ working_hours: wh });
    toast.success("Horário de Segunda copiado para os dias úteis.");
  };

  // time off
  const addTimeOff = () =>
    set({ time_off: [...(draft.time_off || []), { start: "", end: "", reason: "" }] });
  const changeTimeOff = (idx, patch) => {
    const list = [...(draft.time_off || [])];
    list[idx] = { ...list[idx], ...patch };
    set({ time_off: list });
  };
  const removeTimeOff = (idx) =>
    set({ time_off: (draft.time_off || []).filter((_, i) => i !== idx) });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: draft.name,
        active: draft.active,
        bookable: draft.bookable,
        color: draft.color || null,
        working_hours: draft.working_hours || {},
        breaks: draft.breaks || {},
        time_off: (draft.time_off || []).filter((t) => t.start),
        cutoff_by_weekday: Object.fromEntries(
          Object.entries(draft.cutoff_by_weekday || {}).filter(([, v]) => v && v.trim())
        ),
        role: draft.role ?? "",
        bio: draft.bio ?? "",
        photo_url: draft.photo_url ?? "",
        show_on_site: !!draft.show_on_site,
      };
      const res = await api.put(`/admin/professionals/${draft.id}`, payload);
      toast.success("Profissional atualizado.");
      onSaved(res.data);
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Remover o profissional "${draft.name}"?`)) return;
    try {
      await api.delete(`/admin/professionals/${draft.id}`);
      toast.success("Profissional removido.");
      onDeleted(draft.id);
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid={`professional-editor-${draft.id}`} className="bg-white border border-black/10 p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <input
            type="color"
            value={draft.color || "#111111"}
            onChange={(e) => set({ color: e.target.value })}
            className="w-9 h-9 border border-black/15 cursor-pointer p-0.5"
            aria-label="Cor"
          />
          <input
            value={draft.name}
            onChange={(e) => set({ name: e.target.value })}
            className={`${inputCls} text-base font-normal w-52`}
          />
        </div>
        <div className="flex items-center gap-6">
          <Toggle
            checked={draft.active}
            onChange={(v) => set({ active: v })}
            label="Ativo"
            testid={`prof-active-${draft.id}`}
          />
          <Toggle
            checked={draft.bookable}
            onChange={(v) => set({ bookable: v })}
            label="Agendável online"
            testid={`prof-bookable-${draft.id}`}
          />
        </div>
      </div>

      {/* Perfil público (secção Equipa no site) */}
      <div className="bg-white border border-black/10 p-5 -mx-1">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <p className="label-editorial text-black/60 flex items-center gap-2">
              <User size={14} /> Perfil público
            </p>
            <p className="text-xs text-black/50 font-light mt-1">
              Estes campos aparecem na secção <strong>Equipa</strong> do site
              público. Desligue &ldquo;Mostrar no site&rdquo; para esconder este profissional
              da secção pública sem o remover.
            </p>
          </div>
          <Toggle
            checked={!!draft.show_on_site}
            onChange={(v) => set({ show_on_site: v })}
            label="Mostrar no site"
            testid={`prof-show-onsite-${draft.id}`}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <label className="flex flex-col gap-1.5 text-xs font-light text-black/60">
            Função / Cargo
            <input
              data-testid={`prof-role-${draft.id}`}
              type="text"
              value={draft.role || ""}
              onChange={(e) => set({ role: e.target.value })}
              placeholder="Ex.: Founder & CEO"
              className={`${inputCls} w-full`}
            />
          </label>
          <div className="flex flex-col gap-1.5 text-xs font-light text-black/60">
            Foto de perfil
            <div className="flex items-center gap-3">
              {draft.photo_url ? (
                <img
                  src={draft.photo_url}
                  alt={draft.name}
                  className="w-14 h-14 object-cover rounded-full border border-black/10 shrink-0"
                  onError={(e) => (e.currentTarget.style.visibility = "hidden")}
                />
              ) : (
                <div className="w-14 h-14 rounded-full bg-black/5 border border-black/10 flex items-center justify-center text-black/30 shrink-0">
                  <User size={20} />
                </div>
              )}
              <label
                htmlFor={`prof-photo-file-${draft.id}`}
                className={`flex items-center gap-2 border border-black/15 px-4 py-2 cursor-pointer hover:border-black transition-colors text-sm ${uploadingPhoto ? "opacity-50 pointer-events-none" : ""}`}
                data-testid={`prof-photo-upload-${draft.id}`}
              >
                <Upload size={14} /> {uploadingPhoto ? "A carregar…" : "Carregar imagem"}
              </label>
              <input
                id={`prof-photo-file-${draft.id}`}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handlePhotoUpload(e.target.files?.[0])}
              />
              {draft.photo_url && (
                <button
                  type="button"
                  onClick={() => set({ photo_url: "" })}
                  className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1"
                  title="Remover foto"
                >
                  <Trash2 size={13} /> Remover
                </button>
              )}
            </div>
            <details className="mt-1">
              <summary className="text-[11px] text-black/40 cursor-pointer hover:text-black/60">
                Ou colar URL diretamente
              </summary>
              <input
                data-testid={`prof-photo-${draft.id}`}
                type="text"
                value={draft.photo_url || ""}
                onChange={(e) => set({ photo_url: e.target.value })}
                placeholder="/images/team/nome.webp ou https://…"
                className={`${inputCls} w-full mt-2`}
              />
            </details>
          </div>
        </div>
        <label className="flex flex-col gap-1.5 text-xs font-light text-black/60 mt-4">
          Bio curta (1–2 frases)
          <textarea
            data-testid={`prof-bio-${draft.id}`}
            value={draft.bio || ""}
            onChange={(e) => set({ bio: e.target.value })}
            placeholder="Escreva uma pequena descrição pessoal, formação ou abordagem que caracteriza este profissional."
            rows={3}
            className={`${inputCls} w-full resize-none`}
          />
          <span className="text-[10px] text-black/35">{(draft.bio || "").length} caracteres · recomendado até 220</span>
        </label>
      </div>

      {/* Working hours */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="label-editorial text-black/50">Horário de Trabalho</p>
          <button
            type="button"
            onClick={copyMondayToWeekdays}
            className="flex items-center gap-1.5 text-xs text-black/50 hover:text-black"
          >
            <Copy size={13} /> Copiar Seg → dias úteis
          </button>
        </div>
        <div className="space-y-3">
          {DAYS.map(({ key, label }) => (
            <div key={key} className="flex flex-col sm:flex-row sm:items-start gap-2 py-2 border-b border-black/5 last:border-0">
              <div className="w-24 pt-1.5 text-sm font-light text-black/70">{label}</div>
              <div className="flex-1 space-y-2">
                {dayList("working_hours", key).length === 0 && (
                  <span className="text-sm text-black/30 font-light">Folga</span>
                )}
                {dayList("working_hours", key).map((iv, idx) => (
                  <IntervalRow
                    key={idx}
                    interval={iv}
                    testid={`wh-${draft.id}-${key}-${idx}`}
                    onChange={(val) => changeInterval("working_hours", key, idx, val)}
                    onRemove={() => removeInterval("working_hours", key, idx)}
                  />
                ))}
                <button
                  type="button"
                  onClick={() => addInterval("working_hours", key)}
                  className="flex items-center gap-1 text-xs text-black/50 hover:text-black"
                >
                  <Plus size={13} /> Adicionar intervalo
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Breaks */}
      <div>
        <p className="label-editorial text-black/50 mb-3 flex items-center gap-2">
          <Coffee size={14} /> Pausas (por dia)
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
          {DAYS.slice(0, 6).map(({ key, label }) => (
            <div key={key} className="flex flex-col gap-2 py-1">
              <div className="text-sm font-light text-black/70">{label}</div>
              {dayList("breaks", key).map((iv, idx) => (
                <IntervalRow
                  key={idx}
                  interval={iv}
                  testid={`brk-${draft.id}-${key}-${idx}`}
                  onChange={(val) => changeInterval("breaks", key, idx, val)}
                  onRemove={() => removeInterval("breaks", key, idx)}
                />
              ))}
              <button
                type="button"
                onClick={() => addInterval("breaks", key)}
                className="flex items-center gap-1 text-xs text-black/40 hover:text-black w-fit"
              >
                <Plus size={12} /> Pausa
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Time off */}
      <div>
        <p className="label-editorial text-black/50 mb-3 flex items-center gap-2">
          <CalendarOff size={14} /> Folgas / Férias
        </p>
        <div className="space-y-2">
          {(draft.time_off || []).map((t, idx) => (
            <div key={idx} className="flex flex-wrap items-center gap-2" data-testid={`timeoff-${draft.id}-${idx}`}>
              <input type="date" value={t.start || ""} onChange={(e) => changeTimeOff(idx, { start: e.target.value })} className={`${inputCls} w-40`} />
              <span className="text-black/40">até</span>
              <input type="date" value={t.end || ""} onChange={(e) => changeTimeOff(idx, { end: e.target.value })} className={`${inputCls} w-40`} />
              <input value={t.reason || ""} onChange={(e) => changeTimeOff(idx, { reason: e.target.value })} placeholder="Motivo (opcional)" className={`${inputCls} flex-1 min-w-[140px]`} />
              <button type="button" onClick={() => removeTimeOff(idx)} className="p-1.5 text-red-600 hover:bg-black/5" aria-label="Remover folga">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          <button type="button" onClick={addTimeOff} className="flex items-center gap-1 text-xs text-black/50 hover:text-black">
            <Plus size={13} /> Adicionar folga
          </button>
        </div>
      </div>

      {/* Cutoff — prazo para agendar dia seguinte */}
      <div className="bg-amber-50/60 border border-amber-200/70 p-5 -mx-1">
        <div className="flex items-start justify-between gap-4 mb-1">
          <div>
            <p className="label-editorial text-amber-800 flex items-center gap-2">
              <Clock size={14} /> Prazo para agendar o dia seguinte
            </p>
            <p className="text-xs text-black/55 font-light mt-1 max-w-2xl">
              Defina até que hora, em cada dia da semana, se aceitam marcações
              online para o <strong>dia seguinte</strong>. Ex.: se hoje é
              Segunda e o prazo for <strong>17:00</strong>, os clientes só
              conseguem agendar Terça se marcarem antes dessa hora. Deixe vazio
              para não aplicar prazo.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              const val = draft.cutoff_by_weekday?.["0"] || "";
              const wk = { ...(draft.cutoff_by_weekday || {}) };
              ["1", "2", "3", "4", "5"].forEach((k) => (wk[k] = val));
              set({ cutoff_by_weekday: wk });
              toast.success("Prazo de Segunda aplicado aos dias úteis.");
            }}
            className="shrink-0 flex items-center gap-1.5 text-xs text-amber-800/80 hover:text-amber-900"
          >
            <Copy size={13} /> Copiar Seg → dias úteis
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mt-4">
          {DAYS.map(({ key, label }) => (
            <label key={key} className="flex flex-col gap-1 text-xs font-light text-black/60">
              {label}
              <input
                type="time"
                data-testid={`cutoff-${draft.id}-${key}`}
                value={draft.cutoff_by_weekday?.[key] || ""}
                onChange={(e) =>
                  set({
                    cutoff_by_weekday: {
                      ...(draft.cutoff_by_weekday || {}),
                      [key]: e.target.value,
                    },
                  })
                }
                placeholder="—"
                className={`${inputCls} w-full`}
              />
            </label>
          ))}
        </div>
        <p className="text-[11px] text-black/40 mt-3">
          O backoffice consegue sempre agendar, mesmo depois do prazo.
          Este prazo só limita as marcações feitas pelo cliente no site.
        </p>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-2 border-t border-black/5">
        <button
          type="button"
          onClick={remove}
          data-testid={`prof-delete-${draft.id}`}
          className="flex items-center gap-2 text-sm text-red-600 hover:text-red-700"
        >
          <Trash2 size={14} /> Remover
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid={`prof-save-${draft.id}`}
          className="flex items-center gap-2 label-editorial bg-black text-white px-8 py-3 hover:bg-black/80 transition-colors disabled:opacity-50"
        >
          <Save size={15} /> {saving ? "A guardar…" : "Guardar"}
        </button>
      </div>
    </div>
  );
};

export const ProfessionalsAdmin = () => {
  const [professionals, setProfessionals] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [newName, setNewName] = useState("");
  const [loading, setLoading] = useState(true);

  const load = () =>
    api
      .get("/admin/professionals")
      .then((res) => setProfessionals(res.data))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const addProfessional = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      const res = await api.post("/admin/professionals", { name: newName.trim() });
      toast.success("Profissional criado.");
      setNewName("");
      setProfessionals((p) => [...p, res.data].sort((a, b) => a.name.localeCompare(b.name)));
      setOpenId(res.data.id);
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    }
  };

  const onSaved = (updated) =>
    setProfessionals((p) => p.map((x) => (x.id === updated.id ? updated : x)));
  const onDeleted = (id) => {
    setProfessionals((p) => p.filter((x) => x.id !== id));
    setOpenId(null);
  };

  const summary = useMemo(
    () =>
      professionals.map((p) => {
        const days = Object.values(p.working_hours || {}).filter((v) => (v || []).length > 0).length;
        return { ...p, days };
      }),
    [professionals]
  );

  if (loading) return <p className="label-editorial text-black/40">A carregar…</p>;

  return (
    <div data-testid="professionals-panel" className="space-y-6">
      <p className="text-black/50 font-light max-w-2xl">
        Configure a equipa disponível para agendamento online: horário de trabalho, pausas e folgas.
        Só profissionais <strong>ativos e agendáveis</strong> aparecem no site e recebem marcações.
      </p>

      <form onSubmit={addProfessional} className="flex gap-2 items-center">
        <input
          data-testid="new-professional-input"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Nome do novo profissional"
          className={`${inputCls} flex-1 max-w-xs`}
        />
        <button data-testid="add-professional-btn" type="submit" className="flex items-center gap-2 border border-black/20 px-4 py-2 hover:border-black transition-colors">
          <UserPlus size={15} /> Adicionar
        </button>
      </form>

      <div className="space-y-4">
        {summary.map((p) => (
          <div key={p.id} className="border border-black/10">
            <button
              type="button"
              data-testid={`professional-row-${p.id}`}
              onClick={() => setOpenId(openId === p.id ? null : p.id)}
              className="w-full flex items-center justify-between px-6 py-4 bg-white hover:bg-black/[0.02] transition-colors"
            >
              <span className="flex items-center gap-3">
                <span className="w-3 h-3 rounded-full" style={{ background: p.color || "#111" }} />
                <span className="font-normal">{p.name}</span>
                <span className="text-xs text-black/40 font-light">{p.days} dia(s)/semana</span>
              </span>
              <span className="flex items-center gap-2">
                {Object.values(p.cutoff_by_weekday || {}).some((v) => v) && (
                  <span className="text-[10px] px-2 py-0.5 bg-amber-50 text-amber-700 flex items-center gap-1" title="Prazo de agendamento configurado">
                    <Clock size={10} /> Prazo
                  </span>
                )}
                {!p.active && <span className="text-xs px-2 py-0.5 bg-red-50 text-red-600">Inativo</span>}
                {p.active && !p.bookable && <span className="text-xs px-2 py-0.5 bg-amber-50 text-amber-700">Não agendável</span>}
                {p.active && p.bookable && <span className="text-xs px-2 py-0.5 bg-green-50 text-green-700">Disponível</span>}
              </span>
            </button>
            {openId === p.id && (
              <ProfessionalEditor prof={p} onSaved={onSaved} onDeleted={onDeleted} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProfessionalsAdmin;
